import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Slide,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import setNestedValue from 'lodash.set'
import { doc } from 'firebase/firestore'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { getClientTimeZone } from '../../../common/timezones'
import ScheduleSelect from '../../../components/form/ScheduleSelect'
import PatternSelect from '../../../components/form/PatternSelect'
import SymbolSelect from '../../../components/form/SymbolSelect'
import ExitStrategyInputs from '../../backtest/components/exitStrategies/ExitStrategyInputs'
import StopLossStrategySelect from '../../backtest/components/exitStrategies/StopLossStrategySelect'
import RiskPerTradeField from '../../../layout/settingsMenu/sections/AccountSettingsEdit/fields/RiskPerTradeField'
import MaxOpenPositionsField from '../../../layout/settingsMenu/sections/AccountSettingsEdit/fields/MaxOpenPositionsField'
import MaxRiskField from '../../../layout/settingsMenu/sections/AccountSettingsEdit/fields/MaxRiskField'
import DailyTradeLimitField from '../../../layout/settingsMenu/sections/AccountSettingsEdit/fields/DailyTradeLimitField'
import ForceExitField from '../../../layout/settingsMenu/sections/AccountSettingsEdit/fields/ForceExitField'
import { useAlertContext } from '../contexts/AlertContext'
import { useStandardAlertTemplateMutations } from '../hooks/useStandardAlertTemplates'
import StandardAlertTemplate from '../types/StandardAlertTemplate'
import ConfirmDialog from '../../builder/components/sidebar/patternInput/ConfirmDialog'

// Carries every stop-loss strategy's parameters so switching strategies starts from
// sane values instead of blank inputs.
const DEFAULT_TRADING_OPTIONS: NonNullable<StandardAlertTemplate['trading']>['options'] = {
  generateTrades: {
    exitStrategy: 'atr',
    atrChartPeriod: 1,
    atrChartTimeframe: 'hour',
    atrPeriod: 14,
    fractalCount: 2,
    maxPeakValleyLookback: 10,
    maxLookback: 10,
    priceOffset: 0.0001,
    tradeRatios: [{ risk: 1, reward: 2 }],
  } as any,
  analyzeTrades: {
    riskPerTrade: 0.01,
    maxRisk: 0,
    tradeLimit: 0,
    dailyTradeLimit: 0,
    allowHedging: false,
    forceExit: {},
  } as any,
}

function emptyTemplateForm(): Omit<StandardAlertTemplate, 'id' | 'updatedAt' | 'updatedBy'> {
  return {
    name: '',
    patternId: '',
    symbol: 'EURUSD',
    schedule: { timezone: getClientTimeZone(), windows: [] },
    alertTypes: [],
    // New templates are always born off: turning one on is what fans it out to every
    // entitled member, and that runs off the update trigger, so a template created
    // already-on would provision to nobody but future signups.
    autoProvision: false,
    trading: {
      enabled: false,
      accountId: '',
      options: JSON.parse(JSON.stringify(DEFAULT_TRADING_OPTIONS)),
    },
  }
}

// The form dereferences schedule.* and trading.options.* unguarded while rendering, so a
// template doc that predates a field — or was seeded without one — would crash the panel.
// Layer the stored doc over the empty-form defaults rather than trusting it to be complete.
function hydrateTemplateForm(
  template: StandardAlertTemplate
): Omit<StandardAlertTemplate, 'id' | 'updatedAt' | 'updatedBy'> {
  const defaults = emptyTemplateForm()
  const stored = JSON.parse(JSON.stringify(template))
  return {
    ...defaults,
    ...stored,
    // Not inherited from defaults: those carry the create-time off. The backend reads an
    // absent autoProvision as on, so an existing template must mirror that.
    autoProvision: stored.autoProvision !== false,
    schedule: { ...defaults.schedule, ...stored.schedule },
    trading: {
      ...defaults.trading,
      ...stored.trading,
      options: {
        ...stored.trading?.options,
        generateTrades: {
          ...defaults.trading.options.generateTrades,
          ...stored.trading?.options?.generateTrades,
        },
        analyzeTrades: {
          ...defaults.trading.options.analyzeTrades,
          ...stored.trading?.options?.analyzeTrades,
        },
      },
    },
  }
}

function provisioningHelperText(isCreatingTemplate: boolean, autoProvision: boolean): string {
  if (isCreatingTemplate) {
    return 'New templates are created off. Save it, review it, then turn this on to roll it out.'
  }

  return autoProvision
    ? 'New Starter Plus members get an alert from this template. Turning it on also backfills every existing member that lacks it.'
    : 'No new alerts are created from this template. Alerts already provisioned from it keep running and still receive edits made here.'
}

// Keyed on the off -> on transition rather than create-vs-edit, because the backend
// backfill fires on exactly that transition: editing a template that is already on
// propagates to existing alerts but backfills nobody, and promising otherwise would
// overstate what the common edit does.
function saveConfirmDescription(
  isCreatingTemplate: boolean,
  autoProvision: boolean,
  isTurningOn: boolean
): string {
  if (!autoProvision) {
    return isCreatingTemplate
      ? 'This creates the template with provisioning off — no alerts are created from it until you turn it on.'
      : 'Provisioning is off, so no new alerts are created from this template. These changes still apply immediately to every alert already provisioned from it.'
  }

  if (isTurningOn) {
    return 'Turning this on provisions the alert to every Starter Plus member that lacks it, and applies these changes to every alert already provisioned from it.'
  }

  return 'This template is already on, so no members are backfilled. These changes apply immediately to every alert already provisioned from it, and new members keep getting it.'
}

const TemplateSidePanel: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { tradingHouse } = useApplicationSuiteContext()

  const { selectedTemplate, isCreatingTemplate, isTemplateSidePanelOpen, closeTemplateSidePanel } =
    useAlertContext()
  const { saveTemplate, deleteTemplate } = useStandardAlertTemplateMutations()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [mounted, setMounted] = useState(false)
  const [form, setForm] = useState(emptyTemplateForm())
  const [saving, setSaving] = useState(false)
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  const selectedPatternDocRef = useMemo(() => {
    return form.patternId
      ? doc(firebase_firestore, `tradingHouses/${tradingHouse}/standardPatterns/${form.patternId}`)
      : null
  }, [tradingHouse, form.patternId])

  const [selectedPattern, selectedPatternLoading] = useDocumentData(selectedPatternDocRef as any)

  useEffect(() => {
    if (isTemplateSidePanelOpen) {
      setMounted(true)
      setForm(
        isCreatingTemplate || !selectedTemplate
          ? emptyTemplateForm()
          : hydrateTemplateForm(selectedTemplate)
      )
    }
  }, [isTemplateSidePanelOpen, isCreatingTemplate, selectedTemplate])

  const handleTransitionEnd = () => {
    if (!isTemplateSidePanelOpen) {
      setMounted(false)
    }
  }

  if (!mounted) return null

  const updateField = (key: string, value: any) => {
    // lodash.set mutates its first argument, and `{ ...prev }` only shallow-copies the
    // top level — a nested path (e.g. `trading.options.*`) would otherwise mutate
    // prev's nested objects in place. Deep-clone first, same convention already used
    // for DEFAULT_TRADING_OPTIONS and selectedTemplate above.
    setForm((prev) => setNestedValue(JSON.parse(JSON.stringify(prev)), key, value))
  }

  const updateTradingOption = (key: string, value: any) =>
    updateField(`trading.options.${key}`, value)

  // Mirrors the backend's fan-out condition (beforeData.autoProvision === false &&
  // afterData.autoProvision !== false) so the dialog can only promise a backfill when
  // one will actually happen.
  const isTurningOn =
    !isCreatingTemplate && selectedTemplate?.autoProvision === false && form.autoProvision !== false

  const handleSave = async () => {
    setIsSaveConfirmOpen(false)
    setSaving(true)
    try {
      // alertTypes, trading.enabled and trading.accountId are never admin-controlled
      // here — templates only carry trading *settings*; matches the original GTF.ts
      // data, where every hardcoded template had no notification channels, enabled:
      // false and accountId: ''. Force all three regardless of what's in form state.
      // autoProvision is forced off on create for the same reason the toggle is disabled
      // there: the fan-out is an update trigger, so it can only fire on a later edit.
      const dataToSave = {
        ...form,
        alertTypes: [],
        autoProvision: isCreatingTemplate ? false : form.autoProvision !== false,
        trading: { ...form.trading, enabled: false, accountId: '' },
      }
      await saveTemplate(isCreatingTemplate ? undefined : selectedTemplate?.id, dataToSave)
      closeTemplateSidePanel()
    } catch (error) {
      console.error('Error saving template:', error)
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to save template',
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedTemplate) return
    setIsDeleteConfirmOpen(false)
    setSaving(true)
    try {
      await deleteTemplate(selectedTemplate.id)
      closeTemplateSidePanel()
    } catch (error) {
      console.error('Error deleting template:', error)
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to delete template',
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    } finally {
      setSaving(false)
    }
  }

  const panelContent = (
    <Paper
      elevation={4}
      sx={{
        position: 'relative',
        width: isMobile ? '100%' : 400,
        height: '100%',
        backgroundColor: theme.palette.background.custom2 || '#1a1a1a',
        borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(180deg, ${theme.palette.background.custom4} 0%, ${theme.palette.background.custom2} 100%)`,
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {isCreatingTemplate ? 'New Template' : form.name || 'Edit Template'}
        </Typography>
        <IconButton onClick={closeTemplateSidePanel} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={form.autoProvision !== false}
                onChange={(e) => updateField('autoProvision', e.target.checked)}
                disabled={isCreatingTemplate}
              />
            }
            label="Provision to members"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {provisioningHelperText(isCreatingTemplate, form.autoProvision !== false)}
          </Typography>
        </Box>

        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => updateField('name', e.target.value)}
          fullWidth
          size="small"
        />

        <PatternSelect
          standardOnly
          pattern={selectedPattern}
          onSelect={(pattern) => updateField('patternId', pattern ? pattern.id : '')}
        />

        <SymbolSelect
          symbol={form.symbol}
          onSelect={(symbol) => symbol && updateField('symbol', symbol.name)}
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Schedule
          </Typography>
          <ScheduleSelect
            timezone={form.schedule.timezone}
            windows={form.schedule.windows}
            onWindowsChange={(windows) =>
              updateField('schedule', { timezone: form.schedule.timezone, windows })
            }
            backtestMode
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Risk settings
          </Typography>
          <Grid container spacing={2}>
            <RiskPerTradeField
              analyze={form.trading.options.analyzeTrades}
              updateSetting={updateTradingOption}
            />
            <MaxRiskField
              analyze={form.trading.options.analyzeTrades}
              updateSetting={updateTradingOption}
            />
            <MaxOpenPositionsField
              analyze={form.trading.options.analyzeTrades}
              updateSetting={updateTradingOption}
            />
            <DailyTradeLimitField
              analyze={form.trading.options.analyzeTrades}
              updateSetting={updateTradingOption}
            />
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Stop loss
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StopLossStrategySelect
                exitStrategy={form.trading.options.generateTrades.exitStrategy}
                onChange={updateTradingOption}
              />
            </Grid>
            <ExitStrategyInputs
              backtestSettingInputData={form.trading.options}
              handleInputChange={updateTradingOption}
              formErrors={{}}
              symbol={form.symbol}
            />
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Trade Exit
          </Typography>
          <Grid container spacing={2}>
            <ForceExitField
              analyze={form.trading.options.analyzeTrades}
              updateSetting={updateTradingOption}
            />
          </Grid>
        </Box>
      </Box>

      <Box sx={{ p: 2, display: 'flex', gap: 1, borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
        {!isCreatingTemplate && (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setIsDeleteConfirmOpen(true)}
            disabled={saving}
          >
            Delete
          </Button>
        )}
        <Box flexGrow={1} />
        <Button onClick={closeTemplateSidePanel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => setIsSaveConfirmOpen(true)}
          disabled={
            saving ||
            !form.name ||
            !form.patternId ||
            // A template with no windows provisions alerts that can never trigger.
            form.schedule.windows.length === 0 ||
            // Switching to or from Fixed clears the ratios, and an alert needs at least one.
            !form.trading.options.generateTrades.tradeRatios?.length ||
            selectedPatternLoading ||
            (!!form.patternId && !selectedPattern)
          }
        >
          Save
        </Button>
      </Box>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleSave}
        title={isCreatingTemplate ? 'Create this template?' : 'Save changes to this template?'}
        description={saveConfirmDescription(
          isCreatingTemplate,
          form.autoProvision !== false,
          isTurningOn
        )}
        confirmButtonText="Save"
      />

      <ConfirmDialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete this template?"
        description="This removes the template from rotation — it will no longer be used to provision alerts for new Starter Plus members. Alerts already provisioned from it are not affected or deleted."
        confirmButtonText="Delete"
      />
    </Paper>
  )

  return isMobile ? (
    <Drawer
      anchor="right"
      open={isTemplateSidePanelOpen}
      onClose={closeTemplateSidePanel}
      PaperProps={{ sx: { width: '400px', maxWidth: '100%', height: '100%' } }}
    >
      {panelContent}
    </Drawer>
  ) : (
    <Slide
      direction="left"
      in={isTemplateSidePanelOpen}
      mountOnEnter
      unmountOnExit
      onTransitionEnd={handleTransitionEnd}
    >
      {panelContent}
    </Slide>
  )
}

export default TemplateSidePanel
